<template>
  <div class="companies-crm">
    <!-- Hero -->
    <section class="co-hero">
      <div class="co-hero__glow" />
      <div class="d-flex align-items-start justify-content-between flex-wrap gap-12">
        <div class="d-flex align-items-center">
          <div class="co-hero__icon mr-1">
            <feather-icon
              icon="BriefcaseIcon"
              size="24"
            />
          </div>
          <div>
            <h2 class="mb-0 co-hero__title">
              Companies
            </h2>
            <p class="mb-0 co-hero__subtitle">
              Browse registered facilities, their commercial records and requests.
            </p>
          </div>
        </div>
        <div class="co-hero__actions">
          <button
            type="button"
            class="co-btn co-btn--refresh"
            :disabled="dataLoad"
            @click="getAllData(currentPage)"
          >
            <feather-icon
              icon="RefreshCwIcon"
              size="16"
              :class="{ 'co-spin-icon': dataLoad }"
            />
            Refresh
          </button>
        </div>
      </div>

      <div class="co-stats">
        <div class="co-stat">
          <div class="co-stat__icon co-stat__icon--primary">
            <feather-icon
              icon="HomeIcon"
              size="18"
            />
          </div>
          <div>
            <div class="co-stat__value">
              {{ totalRows }}
            </div>
            <div class="co-stat__label">
              Total companies
            </div>
          </div>
        </div>
        <div class="co-stat">
          <div class="co-stat__icon co-stat__icon--info">
            <feather-icon
              icon="FileTextIcon"
              size="18"
            />
          </div>
          <div>
            <div class="co-stat__value">
              {{ totalRequests }}
            </div>
            <div class="co-stat__label">
              Total requests in page
            </div>
          </div>
        </div>
        <div class="co-stat">
          <div class="co-stat__icon co-stat__icon--success">
            <feather-icon
              icon="MailIcon"
              size="18"
            />
          </div>
          <div>
            <div class="co-stat__value">
              {{ withEmailCount }}
            </div>
            <div class="co-stat__label">
              With email in page
            </div>
          </div>
        </div>
        <div class="co-stat">
          <div class="co-stat__icon co-stat__icon--danger">
            <feather-icon
              icon="AlertTriangleIcon"
              size="18"
            />
          </div>
          <div>
            <div class="co-stat__value">
              {{ expiredCount }}
            </div>
            <div class="co-stat__label">
              Expired CR in page
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Toolbar -->
    <div class="co-toolbar">
      <div class="co-toolbar__row">
        <div class="co-search">
          <feather-icon
            icon="SearchIcon"
            size="15"
            class="co-search__icon"
          />
          <input
            v-model="searchQuery"
            type="text"
            class="co-search__input"
            :class="{ 'co-search__input--has-clear': searchQuery }"
            placeholder="Search companies by name, CR number or email… (press Enter)"
            @keyup.enter="onSearch"
          >
          <button
            v-if="searchQuery"
            type="button"
            class="co-search__clear"
            title="Clear search"
            @click="clearSearch"
          >
            <feather-icon
              icon="XIcon"
              size="14"
            />
          </button>
        </div>

        <div class="co-view-switch">
          <button
            type="button"
            class="co-view-switch__btn"
            :class="{ 'co-view-switch__btn--active': viewMode === 'cards' }"
            title="Card view"
            @click="setView('cards')"
          >
            <feather-icon
              icon="GridIcon"
              size="15"
            />
            Cards
          </button>
          <button
            type="button"
            class="co-view-switch__btn"
            :class="{ 'co-view-switch__btn--active': viewMode === 'list' }"
            title="List view"
            @click="setView('list')"
          >
            <feather-icon
              icon="ListIcon"
              size="15"
            />
            List
          </button>
        </div>
      </div>

      <!-- Filters -->
      <div class="co-filters">
        <label class="co-filters__item">
          <span class="co-filters__label">
            <feather-icon
              icon="FileTextIcon"
              size="14"
            />
            CR status
          </span>
          <select
            v-model="crFilter"
            class="co-filter"
          >
            <option :value="null">
              Any
            </option>
            <option value="valid">
              Valid
            </option>
            <option value="soon">
              Expiring soon
            </option>
            <option value="expired">
              Expired
            </option>
            <option value="none">
              No CR date
            </option>
          </select>
        </label>

        <label class="co-filters__item">
          <span class="co-filters__label">
            <feather-icon
              icon="MailIcon"
              size="14"
            />
            Contact info
          </span>
          <select
            :value="contactFilter"
            class="co-filter"
            @change="setContactFilter($event.target.value || null)"
          >
            <option :value="null">
              Any
            </option>
            <option value="email">
              Has email
            </option>
            <option value="no-email">
              No email
            </option>
          </select>
        </label>

        <label class="co-filters__item">
          <span class="co-filters__label">
            <feather-icon
              icon="MapPinIcon"
              size="14"
            />
            Country / city
          </span>
          <input
            v-model="locationFilter"
            type="text"
            class="co-filter co-filter--text"
            placeholder="e.g. UAE, India… (press Enter)"
            @keyup.enter="getAllData(1)"
          >
        </label>

        <label class="co-filters__item">
          <span class="co-filters__label">
            <feather-icon
              icon="ArrowDownIcon"
              size="14"
            />
            Sort by
          </span>
          <select
            v-model="sortBy"
            class="co-filter"
          >
            <option value="name">
              Name
            </option>
            <option value="requests">
              Requests
            </option>
            <option value="cr">
              CR end date
            </option>
          </select>
        </label>

        <!--
          Karim, 23 Sep: restored — "make product filter search the same...
          remove the word renew/new... merge yes and make multi select
          also." Options come from crm/products/filter-options
          (CrmProductController::filterOptions()), NOT crm/products: crm_name
          with a leading "New "/"Renew " stripped (only that, "Scope
          Expansion"/"Surveillance" untouched), products sharing the cleaned
          name merged into one entry ("New SQM" + "Renew SQM" -> "SQM",
          selecting it matches either). serverFilters.productId / product_id
          stay wired for backward compat; productIds/product_ids[] is what
          this control actually sends now.
        -->
        <label class="co-filters__item co-filters__item--search">
          <span class="co-filters__label">
            <feather-icon
              icon="PackageIcon"
              size="14"
            />
            Product
          </span>
          <v-select
            v-model="selectedProductGroups"
            :options="productGroupOptions"
            label="label"
            multiple
            placeholder="Search…"
            @input="onProductGroupsChanged"
          />
        </label>

        <!--
          Karim, 22 Sep: "i just cannot filter by certificate... i need some
          filters." The dropdown above (crm_products, 170 rows) repeats the
          same display name 3+ times for different underlying ids with no way
          to tell them apart, and only covers 17 distinct labels. These two
          use the real catalog instead — same one the Applications Analytics
          dashboard and the CRM's Ongoing Requests page already use (40
          certificate schemes, 106 product categories, no duplicates).
        -->
        <label class="co-filters__item co-filters__item--search">
          <span class="co-filters__label">
            <feather-icon
              icon="AwardIcon"
              size="14"
            />
            Certificate Scheme
          </span>
          <v-select
            :value="serverFilters.certificateScheme || null"
            :options="certificateSchemeOptions"
            placeholder="Any (type to search)"
            @input="v => setServerFilter('certificateScheme', v || '')"
          />
        </label>

        <label class="co-filters__item co-filters__item--search">
          <span class="co-filters__label">
            <feather-icon
              icon="BoxIcon"
              size="14"
            />
            Product Category
          </span>
          <v-select
            :value="serverFilters.productCategory || null"
            :options="productCategoryOptions"
            placeholder="Any (type to search)"
            @input="v => setServerFilter('productCategory', v || '')"
          />
        </label>

        <label class="co-filters__item">
          <span class="co-filters__label">
            <feather-icon
              icon="BriefcaseIcon"
              size="14"
            />
            Activity (industry)
          </span>
          <input
            :value="serverFilters.activityText"
            type="text"
            class="co-filter co-filter--text"
            placeholder="e.g. electrical, toys…"
            @change="setServerFilter('activityText', $event.target.value)"
          >
        </label>

        <label class="co-filters__item">
          <span class="co-filters__label">
            <feather-icon
              icon="StarIcon"
              size="14"
            />
            Rating
          </span>
          <select
            :value="serverFilters.tier"
            class="co-filter"
            @change="setServerFilter('tier', $event.target.value)"
          >
            <option value="">
              Any
            </option>
            <option value="gold">
              Gold
            </option>
            <option value="silver">
              Silver
            </option>
            <option value="bronze">
              Bronze
            </option>
          </select>
        </label>

        <!--
          Karim, 3 Sep: "sort by numbers from lower to bigger or descending" —
          mirror of the Contacts sort. Deliberately its OWN control, not a 4th
          option on "Sort by" above: that one is page-local (re-sorts only the
          rows already loaded — see its own comment), but certificate count
          needs the whole scoped list ordered server-side, same reasoning as
          the filters directly above it.
        -->
        <label class="co-filters__item">
          <span class="co-filters__label">
            <feather-icon
              icon="AwardIcon"
              size="14"
            />
            Sort by certificates
          </span>
          <select
            :value="serverFilters.certSort"
            class="co-filter"
            @change="setServerFilter('certSort', $event.target.value)"
          >
            <option value="">
              Default
            </option>
            <option value="desc">
              Most certificates first
            </option>
            <option value="asc">
              Fewest certificates first
            </option>
          </select>
        </label>

        <span class="co-filters__count">
          Showing <strong>{{ displayedCompanies.length }}</strong> of {{ totalRows }} companies
        </span>

        <button
          type="button"
          class="co-filter co-filter--clear"
          @click="clearFilters"
        >
          <feather-icon
            icon="XIcon"
            size="14"
          />
          Clear filters
        </button>
      </div>

      <!-- All loaded rows ticked, more exist beyond this page — Karim, 2 Sep. -->
      <div
        v-if="allLoadedSelected && !selectAllFiltered && totalRows > companies.length"
        class="co-filter co-filter--select-all"
      >
        <span>All {{ companies.length }} on this page selected.</span>
        <button
          type="button"
          class="co-filter--clear"
          @click="selectAllAcrossFilter"
        >
          Select all {{ totalRows }} matching the filter
        </button>
      </div>

      <!-- Bulk action bar -->
      <div
        v-if="selectionCount"
        class="co-bulk-bar"
      >
        <span class="co-bulk-bar__count">{{ selectionCount }} selected</span>
        <select
          v-model="bulkAssignTo"
          class="co-filter"
          :disabled="bulkBusy"
        >
          <option value="">
            Assign to…
          </option>
          <option value="unassigned">
            Unassigned
          </option>
          <option
            v-for="member in team"
            :key="member.id"
            :value="String(member.id)"
          >
            {{ memberLabel(member) }}
          </option>
        </select>
        <button
          type="button"
          class="co-btn co-bulk-bar__btn"
          :disabled="bulkBusy || !bulkAssignTo"
          @click="bulkAssign"
        >
          Assign
        </button>
        <!-- Karim, 3 Sep: "the multi-select have only [an] assign, but I need
             also to add the rating." -->
        <select
          v-model="bulkTier"
          class="co-filter"
          :disabled="bulkBusy"
        >
          <option value="">
            Rate as…
          </option>
          <option value="gold">
            Gold
          </option>
          <option value="silver">
            Silver
          </option>
          <option value="bronze">
            Bronze
          </option>
          <option value="clear">
            Clear rating
          </option>
        </select>
        <button
          type="button"
          class="co-btn co-bulk-bar__btn"
          :disabled="bulkBusy || !bulkTier"
          @click="bulkSetTier"
        >
          Rate
        </button>
        <button
          v-if="access.canViewTeam"
          type="button"
          class="co-btn co-bulk-bar__btn co-bulk-bar__btn--danger"
          :disabled="bulkBusy"
          @click="bulkDelete"
        >
          <feather-icon
            icon="Trash2Icon"
            size="14"
          />
          Delete
        </button>
        <button
          type="button"
          class="co-bulk-bar__clear"
          :disabled="bulkBusy"
          @click="clearSelection"
        >
          Cancel
        </button>
      </div>

      <!--
        Karim, 3 Sep: "CR DUPLICATE CHECK remove." Removed the standalone
        always-visible manual check box — it duplicated (BRD US 2.3 comment,
        now removed with it) what the search box already triggers
        automatically when a CR-shaped term is typed. That auto-triggered
        warning banner below (`crDuplicate`) still fires from search, so the
        actual duplicate-prevention behaviour BRD §1.1/UAT 2 asked for is
        unchanged — only this extra manual box is gone.
      -->
    </div>

    <!--
      BRD US 2.3: "show a red warning WITH A LINK if it exists".
      It warns and links — it deliberately does NOT block: UAT 2 wants the deal linked
      to the existing facility (a rep's decision), and BRD §1.1 allows several deals on
      the same CR at the same time.
    -->
    <div
      v-if="crDuplicate"
      class="co-dup"
      role="alert"
    >
      <div class="co-dup__icon">
        <feather-icon
          icon="AlertTriangleIcon"
          size="20"
        />
      </div>
      <div class="co-dup__body">
        <h4 class="co-dup__title">
          This CR number already exists
        </h4>
        <p class="co-dup__text">
          {{ crCheck.result.warning }}
        </p>
        <ul
          v-if="crCheck.result.others && crCheck.result.others.length"
          class="co-dup__others"
        >
          <li
            v-for="other in crCheck.result.others"
            :key="other.companyId"
          >
            Also recorded as
            <button
              type="button"
              class="co-dup__linkbtn"
              @click="openFacility(other)"
            >{{ other.name || ('facility #' + other.facilityId) }}</button>
            ({{ other.clients }} contact(s), {{ other.openCards }} open deal(s))
            <span
              v-if="other.merged"
              class="co-dup__retired"
            >retired by a merge</span>
          </li>
        </ul>
        <p
          v-if="crCheck.result.note"
          class="co-dup__note"
        >
          {{ crCheck.result.note }}
        </p>
      </div>
      <div class="co-dup__actions">
        <button
          v-if="crMatchTarget"
          type="button"
          class="co-btn co-btn--danger"
          @click="openFacility(crMatchTarget)"
        >
          <feather-icon
            icon="ExternalLinkIcon"
            size="14"
          />
          Open {{ crMatchTarget.name || ('facility #' + crMatchTarget.facilityId) }}
        </button>
        <button
          v-if="crMatchTarget"
          type="button"
          class="co-btn"
          @click="createOpportunityForMatch"
        >
          <feather-icon
            icon="TargetIcon"
            size="14"
          />
          New opportunity on it
        </button>
        <button
          type="button"
          class="co-btn co-dup__dismiss"
          title="Hide this warning"
          @click="clearCrCheck"
        >
          <feather-icon
            icon="XIcon"
            size="14"
          />
          Dismiss
        </button>
      </div>
    </div>

    <!-- The CR check could not run at all (e.g. its migration has not been applied) -->
    <div
      v-else-if="crCheck.unavailable"
      class="co-dup co-dup--warn"
      role="alert"
    >
      <div class="co-dup__icon">
        <feather-icon
          icon="AlertCircleIcon"
          size="20"
        />
      </div>
      <div class="co-dup__body">
        <h4 class="co-dup__title">
          Duplicate checking is unavailable
        </h4>
        <p class="co-dup__text">
          {{ crCheck.unavailable }}
        </p>
      </div>
    </div>

    <!-- Loading -->
    <div
      v-if="dataLoad"
      class="co-loading"
    >
      <div class="co-loading__spinner" />
      <p class="mb-0 text-muted">
        Loading companies…
      </p>
    </div>

    <!-- Empty -->
    <div
      v-else-if="!displayedCompanies.length"
      class="co-empty"
    >
      <div class="co-empty__icon">
        <feather-icon
          icon="BriefcaseIcon"
          size="30"
        />
      </div>
      <h5 class="mb-50">
        No companies found
      </h5>
      <p class="mb-0 text-muted">
        {{ searchQuery || hasActiveFilters ? 'Try adjusting your search or filters.' : 'Companies will appear here once available.' }}
      </p>
    </div>

    <!-- Grid -->
    <div
      v-else-if="viewMode === 'cards'"
      class="co-grid"
    >
      <article
        v-for="company in displayedCompanies"
        :key="company.id"
        class="co-card"
      >
        <div class="co-card__top">
          <input
            type="checkbox"
            class="co-card__select"
            :checked="isSelected(company)"
            title="Select"
            @click.stop
            @change="toggleSelect(company)"
          >
          <div
            class="co-card__avatar"
            :style="{ background: avatarColor(company) }"
          >
            {{ initials(companyName(company)) }}
          </div>
          <div class="co-card__identity">
            <h3
              class="co-card__name"
              :title="companyName(company)"
              role="button"
              tabindex="0"
              @click="goToShow(company)"
              @keyup.enter="goToShow(company)"
            >
              {{ companyName(company) || 'Unnamed company' }}
            </h3>
            <span
              v-if="secondaryName(company)"
              class="co-card__name-ar"
              :title="secondaryName(company)"
            >
              {{ secondaryName(company) }}
            </span>
            <span class="co-card__id">
              <feather-icon
                icon="HashIcon"
                size="11"
              />
              {{ company.id }}
            </span>
          </div>
          <span
            class="co-cr"
            :class="`co-cr--${crStatus(company.CR_end_date).variant}`"
            :title="`CR end date: ${company.CR_end_date || 'N/A'}`"
          >
            <feather-icon
              :icon="crStatus(company.CR_end_date).icon"
              size="11"
            />
            {{ crStatus(company.CR_end_date).label }}
          </span>
        </div>

        <div class="co-card__body">
          <!-- CR number -->
          <div class="co-cr-line">
            <span class="co-info__icon co-info__icon--primary">
              <feather-icon
                icon="FileTextIcon"
                size="14"
              />
            </span>
            <span
              v-if="company.CR_num"
              class="co-info__text co-cr-line__num"
              :title="`CR Number: ${company.CR_num}`"
            >{{ company.CR_num }}</span>
            <span
              v-else
              class="co-info__text text-muted"
            >No CR number</span>
          </div>

          <!-- email -->
          <a
            v-if="company.email"
            :href="`mailto:${company.email}`"
            class="co-info"
            :title="company.email"
          >
            <span class="co-info__icon co-info__icon--info">
              <feather-icon
                icon="MailIcon"
                size="14"
              />
            </span>
            <span class="co-info__text">{{ company.email }}</span>
          </a>
          <div
            v-else
            class="co-info co-info--empty"
          >
            <span class="co-info__icon co-info__icon--info">
              <feather-icon
                icon="MailIcon"
                size="14"
              />
            </span>
            <span class="co-info__text">No email</span>
          </div>

          <!-- phone -->
          <a
            v-if="company.phone"
            :href="`tel:${company.phone}`"
            class="co-info"
            :title="company.phone"
          >
            <span class="co-info__icon co-info__icon--primary">
              <feather-icon
                icon="PhoneIcon"
                size="14"
              />
            </span>
            <span class="co-info__text">{{ company.phone }}</span>
          </a>
          <div
            v-else
            class="co-info co-info--empty"
          >
            <span class="co-info__icon co-info__icon--primary">
              <feather-icon
                icon="PhoneIcon"
                size="14"
              />
            </span>
            <span class="co-info__text">No phone</span>
          </div>

          <!-- address -->
          <div
            class="co-info"
            :class="{ 'co-info--empty': !company.address }"
            :title="company.address"
          >
            <span class="co-info__icon co-info__icon--warning">
              <feather-icon
                icon="MapPinIcon"
                size="14"
              />
            </span>
            <span class="co-info__text">{{ company.address || 'No address' }}</span>
          </div>

          <!-- postal code -->
          <div
            class="co-info"
            :class="{ 'co-info--empty': !postalCode(company) }"
            :title="`Postal code: ${postalCode(company) || 'N/A'}`"
          >
            <span class="co-info__icon co-info__icon--info">
              <feather-icon
                icon="NavigationIcon"
                size="14"
              />
            </span>
            <span class="co-info__text">{{ postalCode(company) ? `Postal code: ${postalCode(company)}` : 'No postal code' }}</span>
          </div>
        </div>

        <!-- activity tags -->
        <div
          v-if="activityList(company).length"
          class="co-tags"
        >
          <span
            v-for="(act, i) in activityList(company).slice(0, 3)"
            :key="i"
            class="co-tag"
            :title="act"
          >
            <feather-icon
              icon="TagIcon"
              size="10"
            />
            {{ act }}
          </span>
          <span
            v-if="activityList(company).length > 3"
            class="co-tag co-tag--more"
          >
            +{{ activityList(company).length - 3 }} more
          </span>
        </div>
        <div
          v-else
          class="co-tags co-tags--empty"
        >
          No activities listed
        </div>

        <!-- Karim, 3 Sep: "5 client, companies and opportunity in pipeline" —
             our translated status; raw Jeem/Saber text is the tooltip. -->
        <div
          v-if="company.last_request_status_label"
          class="co-card__status"
        >
          <span
            class="co-status-pill"
            :class="`co-status-pill--${company.last_request_status_normalized}`"
            :title="`Jeem/Saber: ${company.last_request_status}`"
          >{{ company.last_request_status_label }}</span>
        </div>

        <!-- footer -->
        <div class="co-card__footer">
          <span class="co-req__count">
            <feather-icon
              icon="LayersIcon"
              size="12"
            />
            <!-- The mirror counts CERTIFICATES, not ERP requests. Say what it is. -->
            {{ requestsCount(company) }} certificate{{ requestsCount(company) === 1 ? '' : 's' }}
          </span>
          <div
            class="d-flex align-items-center"
            style="gap:.4rem"
          >
            <button
              type="button"
              class="co-opp-btn"
              title="Create a CRM opportunity for this company"
              @click.stop="openOpp(company)"
            >
              <feather-icon
                icon="TargetIcon"
                size="13"
              />
              Opportunity
            </button>
            <button
              type="button"
              class="co-card__view"
              title="View company details"
              @click="goToShow(company)"
            >
              <feather-icon
                icon="EyeIcon"
                size="14"
              />
              View
            </button>
          </div>
        </div>
      </article>
    </div>

    <!-- List -->
    <div
      v-else
      class="co-table-wrap"
    >
      <div class="co-table__bar">
        <span class="co-table__bar-icon">
          <feather-icon
            icon="BriefcaseIcon"
            size="18"
          />
        </span>
        <div>
          <h3 class="co-table__bar-title">
            Company Directory
            <span class="co-table__bar-count">{{ totalRows }}</span>
          </h3>
          <span class="co-table__bar-sub">
            Showing {{ displayedCompanies.length }} of {{ totalRows }} companies
          </span>
        </div>
      </div>

      <div class="co-table__scroll">
        <table class="co-table">
          <thead>
            <tr>
              <th class="co-table__th co-table__th--select">
                <input
                  type="checkbox"
                  :checked="allLoadedSelected"
                  title="Select all on this page"
                  @change="toggleSelectAllOnPage"
                >
              </th>
              <th class="co-table__th">
                <feather-icon
                  icon="HomeIcon"
                  size="12"
                />
                Company
              </th>
              <th class="co-table__th">
                <feather-icon
                  icon="FileTextIcon"
                  size="12"
                />
                CR number
              </th>
              <th class="co-table__th">
                <feather-icon
                  icon="ShieldIcon"
                  size="12"
                />
                CR status
              </th>
              <th class="co-table__th">
                <feather-icon
                  icon="MailIcon"
                  size="12"
                />
                Email
              </th>
              <th class="co-table__th">
                <feather-icon
                  icon="PhoneIcon"
                  size="12"
                />
                Phone
              </th>
              <th class="co-table__th co-table__th--center">
                <feather-icon
                  icon="LayersIcon"
                  size="12"
                />
                Certificates
              </th>
              <!--
                Karim, 3 Sep: "there are two statuses in the system... don't
                remove the terminated column and the status of GIM." Shows
                OUR translated status; raw Jeem/Saber text is the tooltip.
              -->
              <th class="co-table__th">
                <feather-icon
                  icon="FlagIcon"
                  size="12"
                />
                Last Status
              </th>
              <th class="co-table__th co-table__th--center">
                <feather-icon
                  icon="SettingsIcon"
                  size="12"
                />
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="company in displayedCompanies"
              :key="company.id"
              class="co-table__row"
            >
              <td class="co-table__td co-table__td--select">
                <input
                  type="checkbox"
                  :checked="isSelected(company)"
                  title="Select"
                  @change="toggleSelect(company)"
                >
              </td>
              <!-- Company -->
              <td class="co-table__td">
                <div class="co-table__company">
                  <div
                    class="co-table__avatar"
                    :style="{ background: avatarColor(company) }"
                  >
                    {{ initials(companyName(company)) }}
                  </div>
                  <div class="co-table__identity">
                    <span
                      class="co-table__name"
                      :title="companyName(company)"
                      role="button"
                      tabindex="0"
                      @click="goToShow(company)"
                      @keyup.enter="goToShow(company)"
                    >{{ companyName(company) || 'Unnamed company' }}</span>
                    <span
                      class="co-table__sub"
                      :title="company.address"
                    >
                      <feather-icon
                        icon="MapPinIcon"
                        size="10"
                      />
                      {{ company.address || 'No address' }}
                    </span>
                  </div>
                </div>
              </td>

              <!-- CR number -->
              <td class="co-table__td">
                <span
                  v-if="company.CR_num"
                  class="co-table__mono"
                >{{ company.CR_num }}</span>
                <span
                  v-else
                  class="co-table__muted"
                >—</span>
              </td>

              <!-- CR status -->
              <td class="co-table__td">
                <span
                  class="co-cr"
                  :class="`co-cr--${crStatus(company.CR_end_date).variant}`"
                  :title="`CR end date: ${company.CR_end_date || 'N/A'}`"
                >
                  <feather-icon
                    :icon="crStatus(company.CR_end_date).icon"
                    size="11"
                  />
                  {{ crStatus(company.CR_end_date).label }}
                </span>
              </td>

              <!-- Email -->
              <td class="co-table__td">
                <a
                  v-if="company.email"
                  :href="`mailto:${company.email}`"
                  class="co-table__link"
                  :title="company.email"
                >{{ company.email }}</a>
                <span
                  v-else
                  class="co-table__muted"
                >No email</span>
              </td>

              <!-- Phone -->
              <td class="co-table__td">
                <a
                  v-if="company.phone"
                  :href="`tel:${company.phone}`"
                  class="co-table__link"
                >{{ company.phone }}</a>
                <span
                  v-else
                  class="co-table__muted"
                >No phone</span>
              </td>

              <!-- Requests -->
              <td class="co-table__td co-table__td--center">
                <span class="co-req__count">
                  <feather-icon
                    icon="LayersIcon"
                    size="12"
                  />
                  {{ requestsCount(company) }}
                </span>
              </td>

              <!-- Last Status -->
              <td class="co-table__td">
                <span
                  v-if="company.last_request_status_label"
                  class="co-status-pill"
                  :class="`co-status-pill--${company.last_request_status_normalized}`"
                  :title="`Jeem/Saber: ${company.last_request_status}`"
                >{{ company.last_request_status_label }}</span>
                <span
                  v-else
                  class="co-table__muted"
                >—</span>
              </td>

              <!-- Actions -->
              <td class="co-table__td co-table__td--center">
                <div class="co-table__actions">
                  <button
                    type="button"
                    class="co-opp-btn co-opp-btn--table"
                    title="Create a CRM opportunity for this company"
                    @click.stop="openOpp(company)"
                  >
                    <feather-icon
                      icon="TargetIcon"
                      size="13"
                    />
                    Opportunity
                  </button>
                  <button
                    type="button"
                    class="co-card__view"
                    title="View company details"
                    @click="goToShow(company)"
                  >
                    <feather-icon
                      icon="EyeIcon"
                      size="14"
                    />
                    View
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- New Opportunity for a company -->
    <div
      v-if="showOppModal"
      class="co-modal"
      @click.self="showOppModal = false"
    >
      <div class="co-modal__box">
        <header class="co-modal__head">
          <h4>New opportunity</h4>
          <button
            type="button"
            class="co-modal__close"
            @click="showOppModal = false"
          >
            <feather-icon
              icon="XIcon"
              size="18"
            />
          </button>
        </header>
        <div class="co-modal__body">
          <p
            class="text-muted mb-1"
            style="font-size:.82rem"
          >
            For <strong>{{ oppCompany && oppCompany.name }}</strong>.
            It will be created in the pipeline and assigned to you.
          </p>
          <div class="form-group">
            <label>Product / service</label>
            <select
              v-model="opp.serviceScheme"
              class="form-control"
              @change="fetchOppPrice"
            >
              <option :value="null">
                — select —
              </option>
              <option
                v-for="s in schemeOptions"
                :key="s"
                :value="s"
              >
                {{ s }}
              </option>
            </select>
            <!--
              MID-CRM W3: this modal never priced anything, so every opportunity
              created from the Companies page was saved with value 0 — which is why
              every money figure on the dashboard read AED 0. Karim's decision:
              auto-fill from the price list, editable.
            -->
            <small
              v-if="oppPriceLoading"
              class="text-muted d-block mt-1"
            >calculating price…</small>
            <small
              v-else-if="oppPrice && oppPrice.isProvisional"
              class="text-primary d-block mt-1"
            >
              This product has tiered fees ({{ oppPrice.currency }}
              {{ oppPrice.priceRange.low }}–{{ oppPrice.priceRange.high }}) — set the value yourself,
              or build the quotation on the card where the tiers can be chosen.
            </small>
            <small
              v-else-if="oppPrice"
              class="text-muted d-block mt-1"
            >
              Price list: {{ oppPrice.currency }} {{ oppPrice.basicPrice }}
              ({{ oppPrice.lineCount }} fee{{ oppPrice.lineCount === 1 ? '' : 's' }})
            </small>
          </div>
          <div class="form-row">
            <div class="form-group col-6">
              <label>Type</label>
              <select
                v-model="opp.type"
                class="form-control"
              >
                <option value="new">
                  New
                </option>
                <option value="renewal">
                  Renewal
                </option>
                <option value="extend">
                  Extend
                </option>
                <option value="survey">
                  Survey
                </option>
              </select>
            </div>
            <div class="form-group col-6">
              <label>Estimated value</label>
              <input
                v-model.number="opp.value"
                type="number"
                min="0"
                class="form-control"
              >
            </div>
          </div>
          <div class="form-group">
            <label>Expected close date</label>
            <input
              v-model="opp.expectedCloseDate"
              type="date"
              class="form-control"
            >
          </div>
          <!-- Karim, 3 Sep: "when creating opportunity you should have the
               list." Manager/admin only — the backend defaults to the client's
               existing owner (or you) when this is left blank. -->
          <div
            v-if="access.canViewTeam"
            class="form-group"
          >
            <label>Assign to</label>
            <select
              v-model="opp.ownerId"
              class="form-control"
            >
              <option value="">
                Myself / keep current owner
              </option>
              <option
                v-for="member in team"
                :key="member.id"
                :value="String(member.id)"
              >
                {{ memberLabel(member) }}
              </option>
            </select>
          </div>
        </div>
        <footer class="co-modal__foot">
          <button
            type="button"
            class="co-btn"
            @click="showOppModal = false"
          >
            Cancel
          </button>
          <button
            type="button"
            class="co-btn co-btn--success"
            :disabled="!opp.serviceScheme || creatingOpp"
            @click="createOpportunity"
          >
            <feather-icon
              icon="TargetIcon"
              size="15"
            />
            {{ creatingOpp ? 'Creating…' : 'Create' }}
          </button>
        </footer>
      </div>
    </div>

    <!-- Pagination -->
    <div
      v-if="!dataLoad"
      class="co-pagination"
    >
      <div class="co-pagination__info">
        Showing
        <span class="font-weight-bold">{{ rangeStart }}</span>
        -
        <span class="font-weight-bold">{{ rangeEnd }}</span>
        of
        <span class="font-weight-bold">{{ totalRows }}</span>
      </div>
      <!-- Karim, 3 Sep: "I want to list all items, everything, not only per
           page." 10000 comfortably covers the whole scoped list. -->
      <label class="co-pagination__size">
        <span>Per page</span>
        <select
          v-model.number="perPage"
          class="co-filter"
          @change="onPageSizeChange"
        >
          <option
            v-for="n in pageSizeOptions"
            :key="n"
            :value="n"
          >
            {{ n }}
          </option>
          <!-- Karim, 18 Sep: "All" (10000) is what makes the page slow when
               nothing is scoping it down — only offer it once a real filter
               narrows the result set. -->
          <option
            v-if="hasServerFilter"
            :value="10000"
          >
            All
          </option>
        </select>
      </label>
      <b-pagination
        v-if="totalRows > perPage"
        v-model="currentPage"
        :total-rows="totalRows"
        :per-page="perPage"
        align="center"
        first-number
        last-number
        @change="onPageChange"
      />
    </div>
  </div>
</template>

<script>
/* eslint-disable camelcase */
import axios from 'axios'
import { BPagination } from 'bootstrap-vue'
import vSelect from 'vue-select'
// Karim, 23 Sep, screenshot: the dropdown rendered broken/unstyled on a hard
// refresh, only looking right after closing and reopening the page — this
// component never actually imported vue-select's own CSS, it was only
// working when some other page (e.g. Leads) that DOES import it happened to
// load first in the same session and leave its CSS in the document. Same
// two imports as the one place in this app that already gets this right
// (AssignDateAndEng.vue) — now loaded with this page's own chunk, not
// borrowed from whichever page loaded before it.
import 'vue-select/dist/vue-select.css'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import decryptData from '@/store/auth/decryptData'

const AVATAR_COLORS = [
  'linear-gradient(135deg, #2563eb, #9e95f5)',
  'linear-gradient(135deg, #00cfe8, #5be0ef)',
  'linear-gradient(135deg, #28c76f, #48da89)',
  'linear-gradient(135deg, #3b82f6, #7dacf8)',
  'linear-gradient(135deg, #ea5455, #f08182)',
  'linear-gradient(135deg, #5a8dee, #7aa5f2)',
]

export default {
  name: 'CompaniesCRM',
  components: { BPagination, vSelect },
  data() {
    return {
      // Was 'factory' — the ERP's raw facility list. That endpoint is shared with
      // the whole admin panel and is NOT access-scoped, so every salesperson saw all
      // 5,277 facilities (including ERP test rows). 'crm/companies' serves the same
      // mirrored facilities but scoped by CrmAccess: own + unclaimed for a rep,
      // everything for a manager. It accepts the same ?skip=&search= this page sends.
      apiUrl: 'crm/companies',
      dataLoad: false,
      companies: [],
      perPage: 12,
      currentPage: 1,
      totalRows: 0,
      searchQuery: '',
      crFilter: null,
      contactFilter: null,
      locationFilter: '',
      sortBy: 'name',
      viewMode: 'cards',
      showOppModal: false,
      oppCompany: null,
      // MID-CRM W3 — price-list lookup for the opportunity modal.
      oppPrice: null,
      oppPriceLoading: false,
      creatingOpp: false,
      schemeOptions: [],
      opp: {
        serviceScheme: null, type: 'new', value: 0, expectedCloseDate: '', ownerId: '',
      },
      // Karim, 2 Sep: "multi select and select all filtered... assign, delete."
      // 3 Sep: "the multi-select have only [an] assign, but I need also to add
      // the rating."
      access: { scope: 'own', canViewTeam: false, seesAll: false },
      team: [],
      selectedIds: [],
      selectAllFiltered: false,
      bulkAssignTo: '',
      bulkTier: '',
      bulkBusy: false,
      /**
       * Karim, 3 Sep: "inside the companies we need the activity that they are
       * working on... we must filter by this, maybe the product items...
       * certificates he does." Real server-side filters — same reasoning as
       * ContactsCRM's serverFilters: they refetch the whole scoped list, not
       * just re-narrow the page-local ones above (crFilter/contactFilter/etc).
       */
      serverFilters: {
        productId: '',
        activityText: '',
        tier: '',
        // Karim, 3 Sep: "sort by numbers from lower to bigger or descending."
        certSort: '',
        // Karim, 22 Sep: the real certificate/product catalog — see the
        // template comment above the Certificate Scheme / Product Category
        // filters for why this exists alongside productId.
        certificateScheme: '',
        productCategory: '',
      },
      productOptions: [],
      certificateSchemeOptions: [],
      productCategoryOptions: [],
      // Karim, 23 Sep: merged product filter — see the template comment
      // above the Product field. Each option is {label, ids: [...]}.
      productGroupOptions: [],
      selectedProductGroups: [],
      // Karim, 3 Sep: "I want to list all items, everything, not only per page."
      pageSizeOptions: [12, 25, 50, 100, 500],
      // BRD US 2.3 — CR duplicate check. `result` is the payload of
      // GET crm/companies/check-cr; `unavailable` holds the backend's reason when the
      // check itself could not run (never treated as "no duplicate").
      crCheck: {
        raw: '', loading: false, result: null, unavailable: '',
      },
    }
  },
  computed: {
    displayedCompanies() {
      let r = [...this.companies]
      if (this.crFilter) {
        r = r.filter(x => this.crStatus(x.CR_end_date).variant === this.crFilter)
      }
      // Karim, 22 Sep: "filter by no email fake as i saw clients with no
      // mail" — this used to re-filter only the current 12-row page, so
      // paging or the total count never reflected it. Now a real server
      // filter (see setContactFilter()/buildFilterParams()); nothing left
      // to do here.
      // Karim, 22 Sep: "i cannot search country as it is by text search and
      // showing no data real" — this searched x.address (blank on most
      // rows) on only the current page. Now a real server search against
      // the actual country column (see buildFilterParams()).
      if (this.sortBy === 'requests') {
        r.sort((a, b) => this.requestsCount(b) - this.requestsCount(a))
      } else if (this.sortBy === 'cr') {
        r.sort((a, b) => String(a.CR_end_date || '9999').localeCompare(String(b.CR_end_date || '9999')))
      } else {
        r.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
      }
      return r
    },
    hasActiveFilters() {
      const sf = this.serverFilters
      return !!(this.crFilter || this.contactFilter || this.locationFilter.trim() || this.sortBy !== 'name'
        || sf.productId || sf.activityText || sf.tier || sf.certSort
        || sf.certificateScheme || sf.productCategory || this.selectedProductGroups.length)
    },
    /** A REAL server-side filter/search (not the page-local ones above) — this,
     * not hasActiveFilters, is what allows the "All" per-page option. */
    hasServerFilter() {
      return !!Object.keys(this.buildFilterParams()).length
    },
    allLoadedSelected() {
      return this.displayedCompanies.length > 0
        && this.displayedCompanies.every(c => this.selectedIds.includes(c.erp_facility_id))
    },
    selectionCount() {
      return this.selectAllFiltered ? this.totalRows : this.selectedIds.length
    },
    totalRequests() {
      return this.companies.reduce((sum, c) => sum + this.requestsCount(c), 0)
    },
    withEmailCount() {
      return this.companies.filter(c => c.email).length
    },
    expiredCount() {
      return this.companies.filter(c => this.crStatus(c.CR_end_date).variant === 'expired').length
    },
    rangeStart() {
      if (!this.totalRows) return 0
      return (this.currentPage - 1) * this.perPage + 1
    },
    rangeEnd() {
      return Math.min(this.currentPage * this.perPage, this.totalRows)
    },
    /**
     * A duplicate worth warning about: either a facility already in the CRM, or (when
     * the caller asked for the deep scan) one that exists in the ERP but is not
     * mirrored yet. Both mean "do not create a new facility".
     */
    crDuplicate() {
      const r = this.crCheck.result
      return !!(r && r.available !== false && (r.exists || r.erp))
    },
    /**
     * The facility the warning links to. The CRM mirror is preferred; an ERP-only hit
     * (found by the deep scan, not mirrored yet) is still linkable because the company
     * page is keyed on the ERP facility id.
     */
    crMatchTarget() {
      const r = this.crCheck.result
      if (!r) return null
      if (r.match) return r.match
      if (r.erp) return { facilityId: r.erp.facilityId, name: r.erp.name }
      return null
    },
  },
  watch: {
    crFilter() { this.syncQuery() },
    contactFilter() { this.syncQuery() },
    locationFilter() { this.syncQuery() },
    sortBy() { this.syncQuery() },
    perPage() { this.syncQuery() },
  },
  mounted() {
    /**
     * Karim, 20 Sep: "open the client itself to see then get back to the table
     * I was in, it reset from beginning." goBack() below now returns via real
     * browser history, which restores whatever URL the list had — but that
     * only helps if every filter actually LIVES in the URL. Before this, only
     * search/view/page did (see syncQuery()'s own old comment: "CR status /
     * contact / location / sort ... are page-local only"). Restore the rest
     * here too, so a real back navigation has something to land on.
     */
    const q = this.$route.query
    if (q.search) this.searchQuery = q.search
    if (q.view === 'list' || q.view === 'cards') this.viewMode = q.view
    if (q.cr) this.crFilter = q.cr
    if (q.contact === 'email' || q.contact === 'no-email') this.contactFilter = q.contact
    if (q.location) this.locationFilter = q.location
    if (q.sort === 'requests' || q.sort === 'cr' || q.sort === 'name') this.sortBy = q.sort
    const initialPerPage = parseInt(q.perPage, 10)
    if (!Number.isNaN(initialPerPage) && this.pageSizeOptions.includes(initialPerPage)) {
      this.perPage = initialPerPage
    }
    if (q.product) this.serverFilters.productId = q.product
    if (q.activity) this.serverFilters.activityText = q.activity
    if (q.tier) this.serverFilters.tier = q.tier
    if (q.certSort) this.serverFilters.certSort = q.certSort
    if (q.certScheme) this.serverFilters.certificateScheme = q.certScheme
    if (q.productCategory) this.serverFilters.productCategory = q.productCategory

    const initialPage = parseInt(this.$route.query.page, 10)
    this.fetchAccess()
    this.fetchProducts()
    this.fetchCertificateCatalog()
    this.fetchProductGroups()
    this.getAllData(Number.isNaN(initialPage) || initialPage < 1 ? 1 : initialPage)
  },
  methods: {
    async fetchAccess() {
      try {
        const res = await axios.get('crm/me')
        const body = decryptData(res.data.payload)
        if (body && body.data) this.access = body.data
        if (this.access.canViewTeam) this.fetchTeam()
      } catch (e) { /* stay on the safe default: own only */ }
    },
    async fetchTeam() {
      try {
        const res = await axios.get('crm/team')
        const body = decryptData(res.data.payload)
        this.team = (body && body.data) || []
      } catch (e) { /* optional */ }
    },
    /** Karim, 2 Sep: role + office next to each name when assigning. */
    memberLabel(member) {
      if (!member) return ''
      // Karim, 3 Sep: "should be markter name - office" — crm/team now
      // defaults to markter-only, so the role parenthetical was just noise
      // repeating "(markter)" on every row. Still shown for the rare ?all=1
      // case (a genuinely mixed-role list) where it's actually informative.
      const office = member.officeName || 'No office'
      const roles = Array.isArray(member.roles) ? member.roles : []
      const mixedRoles = roles.length && !(roles.length === 1 && roles[0] === 'markter')
      return mixedRoles ? `${member.name} (${roles.join('/')}) — ${office}` : `${member.name} — ${office}`
    },
    setView(mode) {
      if (this.viewMode === mode) return
      this.viewMode = mode
      this.syncQuery()
    },
    clearFilters() {
      this.crFilter = null
      const hadContactFilter = !!this.contactFilter
      const hadLocationFilter = !!this.locationFilter.trim()
      this.contactFilter = null
      this.locationFilter = ''
      this.sortBy = 'name'
      const hadProductGroups = !!this.selectedProductGroups.length
      this.selectedProductGroups = []
      const hadServerFilters = hadContactFilter || hadLocationFilter || hadProductGroups
        || this.serverFilters.productId || this.serverFilters.activityText
        || this.serverFilters.tier || this.serverFilters.certSort
        || this.serverFilters.certificateScheme || this.serverFilters.productCategory
      this.serverFilters = {
        productId: '',
        activityText: '',
        tier: '',
        certSort: '',
        certificateScheme: '',
        productCategory: '',
      }
      if (hadServerFilters) this.getAllData(1)
    },
    setServerFilter(key, value) {
      if (this.serverFilters[key] === value) return
      this.serverFilters[key] = value
      this.getAllData(1)
    },
    /** Karim, 22 Sep: Has email / No email is now a real server filter — see buildFilterParams(). */
    setContactFilter(value) {
      if (this.contactFilter === value) return
      this.contactFilter = value
      this.getAllData(1)
    },
    /**
     * BRD US 2.3 / UAT 2 — ask the backend whether a facility with this CR already
     * exists. `deep: 1` also scans the ERP facilities table, because a facility that
     * exists in the ERP but has not been mirrored by crm:sync-clients yet is still a
     * duplicate as far as the rep is concerned.
     */
    async checkCr(raw) {
      const cr = String(raw || '').trim()
      if (!cr) return
      this.crCheck.raw = cr
      this.crCheck.loading = true
      this.crCheck.unavailable = ''
      try {
        const res = await axios.get('crm/companies/check-cr', { params: { cr, deep: 1 }, timeout: 20000 })
        const body = decryptData(res.data.payload)
        const data = (body && body.data) || null
        this.crCheck.result = data
        if (data && data.available === false) {
          // Be explicit: the check did not run, so nothing was proven about this CR.
          this.crCheck.unavailable = data.note || 'The duplicate check is unavailable on this environment.'
        }
      } catch (e) {
        this.crCheck.result = null
        this.crCheck.unavailable = 'The duplicate check could not be reached, so this CR has NOT been verified.'
      } finally {
        this.crCheck.loading = false
      }
    },
    clearCrCheck() {
      this.crCheck.result = null
      this.crCheck.unavailable = ''
    },
    /**
     * Does this search term look like a CR number? Only then is the check fired from
     * the search box, so searching for a company NAME does not spam the endpoint.
     * A CR is essentially digits with optional separators (e.g. "1010-123456").
     */
    looksLikeCr(term) {
      const value = String(term || '')
      if (value.includes('@')) return false
      const compact = value.replace(/[^0-9A-Za-z]/g, '')
      const digits = compact.replace(/[^0-9]/g, '')
      return compact.length >= 5 && digits.length >= 4
    },
    openFacility(match) {
      if (!match || !match.facilityId) return
      this.$router.push({ name: 'crm-system-company-show', params: { id: match.facilityId } })
    },
    /**
     * UAT 2: "the system auto-links the new Deal to the existing Facility, preventing
     * the creation of a duplicate Facility record." This opens the normal opportunity
     * modal already pointed at the EXISTING facility.
     */
    createOpportunityForMatch() {
      const match = this.crMatchTarget
      if (!match) return
      this.openOpp({ id: match.facilityId, name: match.name })
    },
    openOpp(company) {
      this.oppCompany = company
      this.opp = {
        serviceScheme: null, type: 'new', value: 0, expectedCloseDate: '', ownerId: '',
      }
      this.oppPrice = null
      this.showOppModal = true
      if (!this.schemeOptions.length) this.fetchSchemes()
      // Karim, 3 Sep: "when creating opportunity you should have the list."
      // Manager/admin only — a rep can only ever create for himself anyway
      // (the backend enforces this, see CrmPipelineController::store).
      if (this.access.canViewTeam && !this.team.length) this.fetchTeam()
    },

    /**
     * MID-CRM W3 — price the opportunity from the catalogue.
     * A tiered product has no single price, so we do NOT invent one: the value is
     * left for the rep and the range is shown instead (same rule as the pipeline's
     * Add-Card modal, which is where tiers can actually be chosen).
     */
    async fetchOppPrice() {
      this.oppPrice = null
      if (!this.opp.serviceScheme) return
      this.oppPriceLoading = true
      try {
        const res = await axios.get('crm/products/price', {
          params: { scheme: this.opp.serviceScheme },
          timeout: 20000,
        })
        const body = decryptData(res.data.payload)
        this.oppPrice = (body && body.data) || null
        // Only fill a value the rep has not typed, and only when it is decided.
        if (this.oppPrice && !this.oppPrice.isProvisional
          && (!this.opp.value || this.opp.value === 0)) {
          this.opp.value = this.oppPrice.basicPrice || 0
        }
      } catch (e) {
        this.oppPrice = null
      } finally {
        this.oppPriceLoading = false
      }
    },
    async fetchSchemes() {
      try {
        const res = await axios.get('crm/products')
        const body = decryptData(res.data.payload)
        const items = (body && body.data && body.data.products) || []
        this.schemeOptions = [...new Set(items.map(x => x.crmName).filter(Boolean))].sort()
      } catch (e) { /* optional */ }
    },
    async createOpportunity() {
      if (!this.oppCompany) return
      this.creatingOpp = true
      try {
        const cid = this.oppCompany.id
        const owner = this.opp.ownerId ? this.team.find(u => String(u.id) === String(this.opp.ownerId)) : null
        await axios.post('crm/deals', {
          id: `D-F${cid}-${String(this.opp.serviceScheme || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12)}`,
          companyId: cid,
          title: `${this.oppCompany.name} — ${this.opp.serviceScheme}`,
          client: this.oppCompany.name,
          serviceScheme: this.opp.serviceScheme,
          type: this.opp.type,
          value: this.opp.value,
          expectedCloseDate: this.opp.expectedCloseDate || null,
          stage: 'new',
          subStage: 'Qualify need',
          // Karim, 3 Sep: only sent when a manager actually picked someone — the
          // backend (CrmPipelineController::store) already defaults to the
          // creator when this is absent, and ignores it entirely for a rep.
          ...(owner ? { owner: owner.name, ownerId: owner.id } : {}),
        })
        this.showOppModal = false
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Opportunity created', text: `${this.oppCompany.name} — see the Pipeline`, variant: 'success', icon: 'CheckIcon',
          },
        })
      } catch (e) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Failed', text: 'Could not create the opportunity', variant: 'danger', icon: 'AlertCircleIcon',
          },
        })
      } finally {
        this.creatingOpp = false
      }
    },
    /**
     * The ERP's `factory` endpoint returns a translatable name OBJECT ({en, ar});
     * the CRM mirror (crm_companies.name) stores a plain STRING. This page used to
     * read `name.en` only, so after it moved onto the scoped crm/companies endpoint
     * every card read "Unnamed company" while the emails and CR numbers were fine.
     * Accept both shapes.
     */
    companyName(company) {
      if (!company || !company.name) return ''
      if (typeof company.name === 'string') return company.name
      return company.name.en || company.name.ar || ''
    },
    secondaryName(company) {
      if (!company || !company.name) return ''
      if (typeof company.name === 'string') return ''
      const { ar } = company.name
      if (ar && ar !== this.companyName(company)) return ar
      return ''
    },
    postalCode(company) {
      return company.postal_code || company.zip_code || ''
    },
    activityList(company) {
      const activity = company && company.activity
      if (!activity) return []
      const raw = activity.en || activity.ar
      if (!raw || typeof raw !== 'string') return []
      return raw
        .split(/[;,]/)
        .map(item => item.trim())
        .filter(Boolean)
    },
    /**
     * How much history this company has with us.
     *
     * BROKEN FOR WEEKS, AND IT LOOKED LIKE EMPTY DATA. This read
     * `company.requests.length`, an array that only the OLD raw ERP endpoint
     * (/api/admin/factory) returned. When this page was moved onto the CRM mirror
     * (crm/companies) that array stopped existing, so the column rendered 0 for
     * every single company — which reads as "no company has any certificate
     * recorded", not as "the field was renamed".
     *
     * The mirror sends `certificates_count` and `clients_count` from withCount().
     * The legacy array is still honoured so nothing regresses if some other screen
     * hands us an ERP-shaped record.
     *
     * Karim, 21 Sep: "there cannot be zero certificate. Whatever ongoing, not
     * going, already issued, anything must be synced." certificates_count only
     * counts requests that already produced an issued certificate — a company
     * mid-application, or whose last request was rejected, showed a flat 0
     * here, identical to a company with no activity at all. requests_count
     * (backend: CrmCompany::requests(), 21 Sep) is every request regardless of
     * status, so this can't under-report real activity as "0" anymore.
     */
    requestsCount(company) {
      if (!company) return 0
      if (typeof company.requests_count === 'number') return company.requests_count
      if (typeof company.certificates_count === 'number') return company.certificates_count
      if (Array.isArray(company.requests)) return company.requests.length
      return 0
    },

    clientsCount(company) {
      if (!company) return 0
      if (typeof company.clients_count === 'number') return company.clients_count
      return Array.isArray(company.clients) ? company.clients.length : 0
    },
    crStatus(endDate) {
      if (!endDate) {
        return { variant: 'none', label: 'No CR date', icon: 'HelpCircleIcon' }
      }
      const date = new Date(endDate)
      if (Number.isNaN(date.getTime())) {
        return { variant: 'none', label: 'No CR date', icon: 'HelpCircleIcon' }
      }
      const now = new Date()
      const days = Math.ceil((date - now) / (1000 * 60 * 60 * 24))
      if (days < 0) {
        return { variant: 'expired', label: 'CR expired', icon: 'AlertTriangleIcon' }
      }
      if (days <= 60) {
        return { variant: 'soon', label: `CR · ${days}d left`, icon: 'ClockIcon' }
      }
      return { variant: 'valid', label: 'CR valid', icon: 'CheckCircleIcon' }
    },
    initials(name) {
      if (!name) return '??'
      return name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map(part => part.charAt(0).toUpperCase())
        .join('')
    },
    avatarColor(company) {
      const key = company.id || (this.companyName(company) ? this.companyName(company).length : 0)
      return AVATAR_COLORS[key % AVATAR_COLORS.length]
    },
    goToShow(company) {
      /**
       * Route on the ERP FACILITY id, not the CRM row id.
       *
       * `company.id` is the primary key of the crm_companies row; the detail page
       * and its endpoint both work in facility ids — the number everyone actually
       * quotes to each other. Passing the wrong one 404'd every company, which the
       * page then reported as "not in the CRM yet".
       *
       * `facilityId` is accepted too: the merge screen builds its matches that way.
       */
      const id = company.erp_facility_id ?? company.facilityId ?? company.id
      if (!id) return
      this.$router.push({ name: 'crm-system-company-show', params: { id } })
    },
    onSearch() {
      // BRD US 2.3: a rep looking a facility up by CR is exactly the moment the
      // duplicate warning must appear, so searching a CR-shaped term runs the check too.
      const term = this.searchQuery.trim()
      if (this.looksLikeCr(term)) {
        this.crCheck.raw = term
        this.checkCr(term)
      }
      this.getAllData(1)
    },
    clearSearch() {
      this.searchQuery = ''
      this.clearCrCheck()
      this.getAllData(1)
    },
    onPageChange(page) {
      this.getAllData(page)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    /* -------------------- Multi-select + bulk actions -------------------- */
    toggleSelect(company) {
      this.selectAllFiltered = false
      const id = company.erp_facility_id
      const idx = this.selectedIds.indexOf(id)
      if (idx > -1) this.selectedIds.splice(idx, 1)
      else this.selectedIds.push(id)
    },
    isSelected(company) {
      return this.selectedIds.includes(company.erp_facility_id)
    },
    toggleSelectAllOnPage() {
      this.selectAllFiltered = false
      if (this.allLoadedSelected) {
        this.selectedIds = []
      } else {
        this.selectedIds = this.displayedCompanies.map(c => c.erp_facility_id)
      }
    },
    selectAllAcrossFilter() {
      this.selectAllFiltered = true
    },
    clearSelection() {
      this.selectedIds = []
      this.selectAllFiltered = false
      this.bulkAssignTo = ''
      this.bulkTier = ''
    },
    bulkScopeParams() {
      return this.selectAllFiltered
        ? { scope: 'filter', ...this.buildFilterParams() }
        : { scope: 'ids', ids: this.selectedIds }
    },
    async bulkAssign() {
      if (!this.selectionCount || this.bulkBusy) return
      this.bulkBusy = true
      try {
        const res = await axios.post('crm/companies/bulk-action', {
          action: 'assign',
          ownerId: this.bulkAssignTo && this.bulkAssignTo !== 'unassigned' ? this.bulkAssignTo : null,
          ...this.bulkScopeParams(),
        })
        const body = decryptData(res.data.payload)
        const changed = body?.data?.changed ?? 0
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Assigned',
            text: `${changed} compan${changed === 1 ? 'y' : 'ies'} updated`,
            variant: 'success',
            icon: 'CheckIcon',
          },
        })
        this.clearSelection()
        await this.getAllData(this.currentPage)
      } catch (e) {
        this.showError(e, 'Bulk assign failed')
      } finally {
        this.bulkBusy = false
      }
    },
    /** Karim, 3 Sep: "the multi-select have only [an] assign, but I need also
     * to add the rating." Same scope=ids/filter shape as bulkAssign/bulkDelete. */
    async bulkSetTier() {
      if (!this.selectionCount || this.bulkBusy || !this.bulkTier) return
      this.bulkBusy = true
      try {
        const res = await axios.post('crm/companies/bulk-action', {
          action: 'tier',
          // `newTier`, NOT `tier` — see the identical note in ContactsCRM.vue's
          // bulkSetTier(). `tier` is the RATING FILTER, also present when
          // scope=filter spreads buildFilterParams() below.
          newTier: this.bulkTier === 'clear' ? null : this.bulkTier,
          ...this.bulkScopeParams(),
        })
        const body = decryptData(res.data.payload)
        const changed = body?.data?.changed ?? 0
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Rated',
            text: `${changed} compan${changed === 1 ? 'y' : 'ies'} updated`,
            variant: 'success',
            icon: 'CheckIcon',
          },
        })
        this.clearSelection()
        await this.getAllData(this.currentPage)
      } catch (e) {
        this.showError(e, 'Bulk rating failed')
      } finally {
        this.bulkBusy = false
      }
    },
    async bulkDelete() {
      if (!this.selectionCount || this.bulkBusy) return
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Remove ${this.selectionCount} compan${this.selectionCount === 1 ? 'y' : 'ies'} from the CRM? This hides them here — the underlying ERP record is untouched.`)) {
        return
      }
      this.bulkBusy = true
      try {
        const res = await axios.post('crm/companies/bulk-action', {
          action: 'delete',
          ...this.bulkScopeParams(),
        })
        const body = decryptData(res.data.payload)
        const changed = body?.data?.changed ?? 0
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Removed',
            text: `${changed} compan${changed === 1 ? 'y' : 'ies'} removed`,
            variant: 'success',
            icon: 'CheckIcon',
          },
        })
        this.clearSelection()
        await this.getAllData(1)
      } catch (e) {
        this.showError(e, 'Bulk delete failed')
      } finally {
        this.bulkBusy = false
      }
    },
    syncQuery() {
      const search = this.searchQuery.trim()
      const query = {}
      if (this.currentPage > 1) query.page = String(this.currentPage)
      if (search) query.search = search
      if (this.viewMode === 'list') query.view = 'list'
      if (this.crFilter) query.cr = this.crFilter
      if (this.contactFilter) query.contact = this.contactFilter
      if (this.locationFilter.trim()) query.location = this.locationFilter.trim()
      if (this.sortBy !== 'name') query.sort = this.sortBy
      if (this.perPage !== 12) query.perPage = String(this.perPage)
      if (this.serverFilters.productId) query.product = this.serverFilters.productId
      if (this.serverFilters.activityText) query.activity = this.serverFilters.activityText
      if (this.serverFilters.tier) query.tier = this.serverFilters.tier
      if (this.serverFilters.certSort) query.certSort = this.serverFilters.certSort
      if (this.serverFilters.certificateScheme) query.certScheme = this.serverFilters.certificateScheme
      if (this.serverFilters.productCategory) query.productCategory = this.serverFilters.productCategory

      const current = this.$route.query
      const keys = new Set([...Object.keys(current), ...Object.keys(query)])
      const changed = [...keys].some(k => current[k] !== query[k])
      if (changed) {
        this.$router.replace({ query }).catch(() => {})
      }
    },
    /**
     * The filter set that defines "everyone currently matching the screen" —
     * used both for the paginated fetch and bulk-action's scope=filter (Karim, 2
     * Sep: "select all filtered"). CR status / contact / location / sort stay
     * page-local (client-side re-filter of the already-fetched page, not a
     * re-fetch) — they round-trip through the URL (syncQuery/mounted) since
     * 20 Sep so "back" restores them, but they are not sent to the server here.
     */
    buildFilterParams() {
      const params = {}
      const search = this.searchQuery.trim()
      if (search) params.search = search
      if (this.serverFilters.productId) params.product_id = this.serverFilters.productId
      if (this.selectedProductGroups.length) {
        params.product_ids = this.selectedProductGroups.flatMap(g => g.ids)
      }
      if (this.serverFilters.activityText) params.activityText = this.serverFilters.activityText
      if (this.serverFilters.tier) params.tier = this.serverFilters.tier
      if (this.serverFilters.certificateScheme) params.certificateScheme = this.serverFilters.certificateScheme
      if (this.serverFilters.productCategory) params.productCategory = this.serverFilters.productCategory
      if (this.contactFilter === 'email') params.hasEmail = 'yes'
      if (this.contactFilter === 'no-email') params.hasEmail = 'no'
      if (this.locationFilter.trim()) params.location = this.locationFilter.trim()
      if (this.serverFilters.certSort) {
        params.sortBy = 'certificates'
        params.sortDir = this.serverFilters.certSort
      }
      return params
    },
    /** Karim, 3 Sep: "I want to list all items, everything, not only per page." */
    onPageSizeChange() {
      this.getAllData(1)
    },
    async fetchProducts() {
      try {
        const res = await axios.get('crm/products')
        const body = decryptData(res.data.payload)
        this.productOptions = Array.isArray(body?.data?.products) ? body.data.products : []
      } catch (e) {
        this.productOptions = []
      }
    },
    /** Karim, 22 Sep: real certificate/product catalog — see template comment. */
    async fetchCertificateCatalog() {
      try {
        const res = await axios.get('crm/ongoing-requests/filters')
        const body = decryptData(res.data.payload)
        this.certificateSchemeOptions = body?.data?.certificateSchemes || []
        this.productCategoryOptions = body?.data?.productCategories || []
      } catch (e) {
        this.certificateSchemeOptions = []
        this.productCategoryOptions = []
      }
    },
    /** Karim, 23 Sep: merged product filter options — see fetchProducts() vs this. */
    async fetchProductGroups() {
      try {
        const res = await axios.get('crm/products/filter-options')
        const body = decryptData(res.data.payload)
        this.productGroupOptions = Array.isArray(body?.data?.options) ? body.data.options : []
      } catch (e) {
        this.productGroupOptions = []
      }
    },
    onProductGroupsChanged() {
      this.getAllData(1)
    },
    async getAllData(page = 1) {
      // Karim, 18 Sep, corrected same day: the actual ask was never "hide the
      // list by default" — it was "the 'All' (10000) per-page option is what
      // makes this slow; cap per-page at 500 until a real filter/search is
      // applied, only allow 'All' once one is." Show the normal paginated
      // list by default; just never let perPage exceed 500 without a filter.
      const filterParams = this.buildFilterParams()
      if (!Object.keys(filterParams).length && this.perPage > 500) {
        this.perPage = 500
      }

      this.currentPage = page
      this.dataLoad = true
      this.syncQuery()
      this.clearSelection()
      try {
        const params = {
          skip: (page - 1) * this.perPage,
          limit: this.perPage,
          ...filterParams,
        }

        const response = await axios.get(this.apiUrl, { params })
        if (response.status === 200) {
          const responseData = decryptData(response.data.payload)
          const data = responseData?.data || {}
          const listKey = Object.keys(data).find(key => Array.isArray(data[key]))
          const list = listKey ? data[listKey] : []
          this.companies = Array.isArray(list) ? list : []
          this.totalRows = data.count ?? this.companies.length
        }
      } catch (e) {
        this.showError(e, 'Failed to load companies')
      } finally {
        this.dataLoad = false
      }
    },
    showError(error, fallback) {
      let message = fallback
      try {
        if (error?.response?.data?.payload) {
          const payload = decryptData(error.response.data.payload)
          const msg = payload?.message || payload
          if (typeof msg === 'string') message = msg
        }
      } catch {
        // keep fallback
      }
      this.$toast({
        component: ToastificationContent,
        props: {
          title: message,
          icon: 'BellIcon',
          variant: 'danger',
        },
      })
    },
  },
}
</script>

<style lang="scss" src="./companies-crm.scss"></style>
<style lang="scss" scoped>
// Karim, 23 Sep, screenshot: theme styling for vue-select (chevron sizing,
// input height, colors matching this app) — same source as
// AssignDateAndEng.vue, the one other place already doing this right.
@import "@core/scss/vue/libs/vue-select.scss";

.co-toolbar__row {
  display: flex; align-items: center; justify-content: space-between;
  gap: .75rem; flex-wrap: wrap;
}
.co-toolbar__row .co-search { flex: 1 1 260px; }

/* view switch — same shape as the Contacts page */
.co-view-switch {
  display: inline-flex; align-items: center; gap: .15rem;
  background: #f5f5f9; border-radius: .4rem; padding: .2rem;
}
.co-view-switch__btn {
  display: inline-flex; align-items: center; gap: .3rem; border: 0; cursor: pointer;
  background: transparent; color: #6e6b7b; font-size: .78rem; font-weight: 600;
  padding: .35rem .7rem; border-radius: .3rem;
}
.co-view-switch__btn--active { background: #fff; color: #2563eb; box-shadow: 0 1px 3px rgba(34, 41, 47, .12); }

.co-filters {
  display: flex; gap: .6rem; flex-wrap: wrap; align-items: center;
  margin-top: .75rem; padding-top: .75rem; border-top: 1px solid #f0f0f5;
}
.co-filters__item { display: flex; align-items: center; gap: .35rem; margin-bottom: 0; }
// Karim, 22 Sep: "i need to search with letters" — vue-select (already used
// elsewhere in the CRM, e.g. LeadsCRM's campaign picker) replaces the plain
// native <select> for these two, same filter bar row.
.co-filters__item--search { min-width: 230px; }
.co-filters__item--search .v-select { flex: 1; font-size: .78rem; }
// Karim, 22 Sep, screenshot: the dropdown menu was clamped to the trigger's
// own ~230px width, so long option text ("Technical Regulation for Building
// Materials...") wrapped across several cramped lines. Let the menu size to
// its content instead — width auto, never narrower than the trigger, capped
// so it can't run off-screen on a small viewport.
.co-filters__item--search .v-select ::v-deep .vs__dropdown-menu {
  width: auto;
  min-width: 100%;
  max-width: 480px;
  white-space: normal;
}
.co-filters__label {
  display: inline-flex; align-items: center; gap: .25rem;
  font-size: .72rem; font-weight: 600; color: #6e6b7b;
  text-transform: uppercase; letter-spacing: .3px;
}
.co-filters__count { margin-left: auto; font-size: .76rem; color: #6e6b7b; }
.co-filter {
  border: 1px solid #e4e4ec; border-radius: .35rem; padding: .3rem .5rem;
  font-size: .78rem; background: #fff; color: #5e5873; cursor: pointer; outline: none;
}
.co-filter:focus { border-color: #2563eb; }
.co-filter--text { cursor: text; min-width: 130px; }
.co-filter--clear {
  display: inline-flex; align-items: center; gap: .3rem; font-weight: 600;
  color: #ea5455; border-color: rgba(234, 84, 85, .35); background: rgba(234, 84, 85, .08);
}

/* Bulk action bar (Karim, 2 Sep: multi-select + select-all-filtered) */
.co-filter--select-all {
  display: flex; align-items: center; gap: .6rem;
  padding: .5rem .8rem; border-radius: .5rem; margin-top: .5rem;
  background: linear-gradient(135deg, #dfeafd 0%, #c5dafc 100%);
  border: 1px solid #b0c9f1; font-size: .8rem; color: #5e5873;
}
.co-filter--select-all button {
  border: none; background: #fff; color: #144faf; font-weight: 700;
  padding: .3rem .7rem; border-radius: .4rem; cursor: pointer; font-size: .76rem;
}
.co-bulk-bar {
  display: flex; align-items: center; gap: .6rem; flex-wrap: wrap;
  padding: .55rem .8rem; border-radius: .5rem; margin-top: .5rem;
  background: #2d2d3f; color: #fff;
}
.co-bulk-bar__count { font-size: .82rem; font-weight: 700; margin-right: .3rem; }
.co-bulk-bar__btn { padding: .4rem .9rem; font-size: .78rem; }
.co-bulk-bar__btn--danger { background: #ea5455; color: #fff; border-color: #ea5455; }
.co-bulk-bar__btn--danger:hover { background: #d93025; }
.co-bulk-bar__clear {
  margin-left: auto; background: transparent; border: none; color: #c7c7d9;
  font-size: .78rem; font-weight: 600; cursor: pointer;
}
.co-bulk-bar__clear:hover { color: #fff; }
.co-card__select {
  width: 16px; height: 16px; margin-right: .55rem; flex-shrink: 0;
  cursor: pointer; align-self: flex-start;
}
.co-table__th--select, .co-table__td--select { width: 34px; text-align: center; }

/* ---- list view ---- */
.co-table-wrap {
  background: #fff; border: 1px solid #f0f0f5; border-radius: .6rem;
  box-shadow: 0 2px 10px rgba(34, 41, 47, .05); overflow: hidden; margin-bottom: 1rem;
}
.co-table__bar {
  display: flex; align-items: center; gap: .6rem;
  padding: .85rem 1rem; border-bottom: 1px solid #f0f0f5;
}
.co-table__bar-icon {
  display: inline-flex; align-items: center; justify-content: center;
  width: 34px; height: 34px; border-radius: .45rem;
  background: rgba(37, 99, 235, .12); color: #2563eb;
}
.co-table__bar-title {
  margin: 0; font-size: .95rem; font-weight: 600; color: #5e5873;
  display: flex; align-items: center; gap: .4rem;
}
.co-table__bar-count {
  background: rgba(37, 99, 235, .12); color: #2563eb;
  font-size: .7rem; font-weight: 700; padding: .1rem .4rem; border-radius: .8rem;
}
.co-table__bar-sub { font-size: .75rem; color: #6e6b7b; }
.co-table__scroll { overflow-x: auto; }
.co-table { width: 100%; border-collapse: collapse; min-width: 940px; }
.co-table__th {
  text-align: left; padding: .6rem .8rem; background: #f8f8fb;
  font-size: .68rem; font-weight: 700; text-transform: uppercase; letter-spacing: .4px;
  color: #6e6b7b; white-space: nowrap; border-bottom: 1px solid #f0f0f5;
}
.co-table__th--center { text-align: center; }
.co-table__row { border-bottom: 1px solid #f4f4f8; }
.co-table__row:hover { background: #fafaff; }
.co-table__td { padding: .6rem .8rem; font-size: .8rem; color: #5e5873; vertical-align: middle; }
.co-table__td--center { text-align: center; }
.co-table__company { display: flex; align-items: center; gap: .55rem; }
.co-table__avatar {
  width: 34px; height: 34px; border-radius: .45rem; flex: 0 0 34px;
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-size: .74rem; font-weight: 700;
}
.co-table__identity { display: flex; flex-direction: column; min-width: 0; }
.co-table__name {
  font-weight: 600; color: #5e5873; cursor: pointer; max-width: 240px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.co-table__name:hover { color: #2563eb; }
.co-table__sub {
  display: inline-flex; align-items: center; gap: .2rem;
  font-size: .68rem; color: #b9b9c3; max-width: 240px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.co-table__mono { font-family: monospace; font-size: .78rem; }
.co-table__muted { color: #b9b9c3; font-size: .76rem; }
.co-table__link {
  color: #00cfe8; max-width: 200px; display: inline-block;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap; vertical-align: bottom;
}
.co-table__actions { display: inline-flex; align-items: center; gap: .4rem; }
.co-opp-btn--table { margin-top: 0; }

.co-opp-btn {
  display: inline-flex; align-items: center; gap: .3rem; border: 0; cursor: pointer;
  background: #28c76f; color: #fff; font-size: .74rem; font-weight: 600;
  padding: .3rem .55rem; border-radius: .3rem; margin-top: .5rem;
}
.co-opp-btn:hover { filter: brightness(.95); }
.co-modal {
  position: fixed; top: 0; right: 0; bottom: 0; left: 0; background: rgba(34,41,47,.5);
  z-index: 1050; display: flex; align-items: center; justify-content: center; padding: 1rem;
}
.co-modal__box { background: #fff; border-radius: .6rem; width: 100%; max-width: 460px; display: flex; flex-direction: column; max-height: 92vh; }
.co-modal__head { display: flex; justify-content: space-between; align-items: center; padding: .9rem 1.1rem; border-bottom: 1px solid #f0f0f5; }
.co-modal__head h4 { margin: 0; font-size: 1rem; }
.co-modal__body { padding: 1rem 1.1rem; overflow-y: auto; }
.co-modal__foot { display: flex; justify-content: flex-end; gap: .5rem; padding: .8rem 1.1rem; border-top: 1px solid #f0f0f5; }
.co-modal__close { border: 0; background: transparent; cursor: pointer; color: #6e6b7b; }
.co-btn {
  display: inline-flex; align-items: center; gap: .35rem; border: 0; cursor: pointer;
  padding: .45rem .8rem; border-radius: .35rem; font-size: .82rem; font-weight: 600;
  background: #f1f1f4; color: #5e5873;
}
.co-btn--success { background: #28c76f; color: #fff; }
.co-btn--danger { background: #ea5455; color: #fff; }
.co-btn:disabled { opacity: .5; cursor: not-allowed; }

/* ---- BRD US 2.3: CR duplicate check + red warning ---- */
.co-crcheck {
  display: flex; align-items: center; gap: .5rem; flex-wrap: wrap;
  margin-top: .6rem; padding-top: .6rem; border-top: 1px dashed #f0f0f5;
}
.co-crcheck__label {
  display: inline-flex; align-items: center; gap: .25rem;
  font-size: .72rem; font-weight: 700; color: #ea5455;
  text-transform: uppercase; letter-spacing: .3px;
}
.co-crcheck__input { min-width: 230px; }
.co-crcheck__btn { padding: .3rem .6rem; font-size: .78rem; }
.co-crcheck__clear-ok {
  display: inline-flex; align-items: center; gap: .25rem;
  font-size: .75rem; color: #28c76f; font-weight: 600;
}
/* .co-spin-icon / @keyframes co-spin already live in companies-crm.scss — reused here. */

.co-dup {
  display: flex; align-items: flex-start; gap: .75rem;
  background: rgba(234, 84, 85, .08); border: 1px solid rgba(234, 84, 85, .45);
  border-left: 4px solid #ea5455; border-radius: .5rem;
  padding: .8rem 1rem; margin-bottom: 1rem;
}
.co-dup--warn {
  background: rgba(59, 130, 246, .08); border-color: rgba(59, 130, 246, .45);
  border-left-color: #3b82f6;
}
.co-dup__icon { color: #ea5455; flex: 0 0 auto; }
.co-dup--warn .co-dup__icon { color: #3b82f6; }
.co-dup__body { flex: 1 1 260px; min-width: 0; }
.co-dup__title { margin: 0 0 .2rem; font-size: .92rem; font-weight: 700; color: #ea5455; }
.co-dup--warn .co-dup__title { color: #3b82f6; }
.co-dup__text { margin: 0; font-size: .82rem; color: #5e5873; }
.co-dup__others { margin: .4rem 0 0; padding-left: 1.1rem; font-size: .78rem; color: #6e6b7b; }
.co-dup__linkbtn {
  border: 0; background: transparent; padding: 0; cursor: pointer;
  color: #ea5455; font-weight: 600; text-decoration: underline;
}
.co-dup__retired {
  background: rgba(110, 107, 123, .12); color: #6e6b7b;
  font-size: .68rem; font-weight: 700; padding: .05rem .35rem; border-radius: .8rem;
}
.co-dup__note { margin: .4rem 0 0; font-size: .74rem; color: #6e6b7b; font-style: italic; }
.co-dup__actions { display: flex; flex-direction: column; gap: .35rem; flex: 0 0 auto; }
.co-dup__dismiss { background: transparent; color: #6e6b7b; }
</style>
